// src/components/SmartLists/smartListsGridConfig.js

/**
 * smartListsGridConfig
 * -----------------------------------------------------------------------------
 * Centralized spreadsheet/grid configuration for the SmartLists workspace.
 *
 * PURPOSE
 *  • Keep all shared spreadsheet sizing and column definitions in one place
 *  • Ensure grouped SmartList column blocks stay consistent across components
 *  • Prepare the canvas for future row/column resize behavior
 *  • Prevent hard-coded dimensions from drifting across the codebase
 *
 * IMPORTANT
 *  • SmartLists still uses grouped columns by field (one "column group" per field)
 *  • Each field-group uses the same spreadsheet columns:
 *      - Option
 *      - Hint
 *      - Status
 *      - Launch Date
 *      - End Date
 *  • These definitions are intentionally centralized so future changes
 *    (adding request status, owner, approval state, etc.) happen in one place
 * -----------------------------------------------------------------------------
 */

/* ---------------------------------------------------------------------------
 * Visible sheet sizing
 * ---------------------------------------------------------------------------
 * These values are intentionally tighter now so the UI feels closer to
 * Google Sheets:
 *  • shorter rows
 *  • shorter headers
 *  • denser spreadsheet layout
 * ------------------------------------------------------------------------- */
export const MIN_ROWS = 50;

export const ROW_NUMBER_WIDTH = 40;
export const GROUP_HEADER_HEIGHT = 32;
export const COLUMN_HEADER_HEIGHT = 26;
export const GRID_ROW_HEIGHT = 24;

/* ---------------------------------------------------------------------------
 * SmartLists grouped spreadsheet columns
 * ---------------------------------------------------------------------------
 * NOTES
 *  • `key`       -> row object property
 *  • `label`     -> visible column label
 *  • `width`     -> default spreadsheet width
 *  • `minWidth`  -> lower bound when resizing like a spreadsheet
 *  • `type`      -> editor/rendering type
 *
 * SPREADSHEET UX NOTE
 *  • These defaults are intentionally tighter so new columns start closer
 *    to a Google Sheets-like compact width.
 * ------------------------------------------------------------------------- */
export const COLUMN_CONFIG = [
  {
    key: 'label',
    label: 'Option',
    width: 110,
    minWidth: 56,
    type: 'text',
  },
  {
    key: 'hint',
    label: 'Hint',
    width: 110,
    minWidth: 56,
    type: 'text',
  },
  {
    key: 'liveFrom',
    label: 'Launch Date',
    width: 110,
    minWidth: 64,
    type: 'date-text',
  },
  {
    key: 'liveTo',
    label: 'End Date',
    width: 110,
    minWidth: 64,
    type: 'date-text',
  },
  {
    key: 'lifecycleStatus',
    label: 'Status',
    width: 90,
    minWidth: 52,
    type: 'select',
  },
];

/* ---------------------------------------------------------------------------
 * Status values for org-managed options
 * ------------------------------------------------------------------------- */
export const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
  { value: 'archived', label: 'Archive' },
];

/* ---------------------------------------------------------------------------
 * Helper: default column width map
 * ------------------------------------------------------------------------- */
export const buildDefaultColumnWidths = () => {
  return COLUMN_CONFIG.reduce((acc, column) => {
    acc[column.key] = column.width;
    return acc;
  }, {});
};

/* ---------------------------------------------------------------------------
 * Helper: total width of a single SmartList field-group
 * ------------------------------------------------------------------------- */
export const getColumnGroupWidth = (columnWidths = null) => {
  return COLUMN_CONFIG.reduce((sum, column) => {
    const width = columnWidths?.[column.key] || column.width;
    return sum + width;
  }, 0);
};